import { useEffect, useState, type RefObject } from "react";

export interface TextSelectionState {
  text: string;
  rect: DOMRect;
  range: Range;
}

const MIN_CHARS = 10;

/**
 * Observe la sélection native dans un container donné.
 * Retourne null si :
 * - la sélection est vide / collapsed
 * - elle est hors du container
 * - elle fait moins de MIN_CHARS caractères
 */
export function useTextSelection(
  containerRef: RefObject<HTMLElement | null>,
): TextSelectionState | null {
  const [selection, setSelection] = useState<TextSelectionState | null>(null);

  useEffect(() => {
    function handleSelectionChange() {
      const sel = typeof window !== "undefined" ? window.getSelection() : null;
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setSelection(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const container = containerRef.current;
      if (!container || !container.contains(range.commonAncestorContainer)) {
        setSelection(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < MIN_CHARS) {
        setSelection(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setSelection(null);
        return;
      }
      setSelection({ text, rect, range: range.cloneRange() });
    }

    document.addEventListener("selectionchange", handleSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", handleSelectionChange);
  }, [containerRef]);

  return selection;
}
